// InventoryFilterPanel.js
// Simple filter panel for filtering products by total inventory threshold

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  Collapse,
  Chip,
  Badge,
  Stack,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const InventoryFilterPanel = ({
  onApply,
  appliedThreshold = null,
  storageKey = 'products-inventory-filter',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState('');

  const hasActiveFilter = appliedThreshold != null && appliedThreshold !== '';

  // Handle toggle - sync when opening
  const handleToggle = () => {
    if (!isOpen) {
      setEditingThreshold(hasActiveFilter ? String(appliedThreshold) : '');
    }
    setIsOpen(prev => !prev);
  };

  // Apply filter
  const handleApply = () => {
    const value = editingThreshold ? Number(editingThreshold) : null;
    onApply(value);

    try {
      if (value != null) {
        sessionStorage.setItem(storageKey, String(value));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.error('Failed to save inventory filter to sessionStorage', e);
    }

    setIsOpen(false);
  };

  // Handle Enter key
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  // Cancel and close
  const handleCancel = () => {
    setIsOpen(false);
  };

  // Reset form
  const handleReset = () => {
    setEditingThreshold('');
  };

  // Clear applied filter
  const handleClearFilter = () => {
    onApply(null);
    sessionStorage.removeItem(storageKey);
  };

  const getFilterChipLabel = () => {
    if (!hasActiveFilter) return '';
    const formattedNumber = parseFloat(appliedThreshold).toLocaleString('he-IL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `מלאי >= ${formattedNumber}`;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Badge badgeContent={hasActiveFilter ? 1 : 0} color="primary" invisible={!hasActiveFilter}>
          <Button
            variant={isOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={handleToggle}
            size="small"
            sx={{ direction: 'ltr' }}
          >
            סינון מלאי
          </Button>
        </Badge>

        {!isOpen && hasActiveFilter && (
          <Chip
            label={getFilterChipLabel()}
            onDelete={handleClearFilter}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ direction: 'ltr' }}
          />
        )}
      </Box>

      <Collapse
        in={isOpen}
        sx={{
          position: { xs: 'static', sm: 'absolute' },
          top: { sm: '100%' },
          right: { sm: 0 },
          zIndex: 10,
          mt: 1,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Paper sx={{ p: 2, minWidth: { xs: 0, sm: 300 }, width: { xs: '100%', sm: 'auto' } }} elevation={3}>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="סה״כ מלאי מינימלי"
              type="number"
              value={editingThreshold}
              onChange={(e) => setEditingThreshold(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => e.target.select()}
              fullWidth
              inputProps={{
                min: 0,
                step: 1,
              }}
            />
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              sx={{ direction: 'ltr' }}
            >
              אפס
            </Button>
            <Button size="small" color="inherit" onClick={handleCancel}>
              ביטול
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
            >
              סינון
            </Button>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default InventoryFilterPanel;
